import useDebounce from 'hooks/useDebounce';
import { useEffect, useState, useRef } from 'react';
import {
  CATEGORIES_ID,
  EMOJI_CDN,
  EMOJI_LIST_ID,
  EMOJI_LOCALSTORAGE_KEY,
  EMOJI_PICKER_CATEGORY,
  LIMIT_FREQUENT_EMOJIS,
  SKINS_ID,
} from './constants';

function useEmojiPicker() {
  const lastScrollPos = useRef(1);

  const [activeIndexCategory, setActiveIndexCategory] = useState(0);
  const [listEmoji, setListEmoji] = useState([]);
  const [recentListEmoji, setRecentListEmoji] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');

  const keywordDebounce = useDebounce(searchKeyword, 300);

  const handleClickCategory = (category) => {
    const element = document.getElementById('emoji-list');
    element.getElementsByClassName(`${category}`)[0].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  const transformToCategoryView = (emojiData, skinId) => {
    if (!emojiData) {
      return [];
    }

    const result = emojiData.categories
      .filter(({ id }) => id !== CATEGORIES_ID.FLAGS)
      .map(({ emojis, id }) => ({
        id,
        emojis: emojis.map((emojiId) => {
          const emoji = emojiData.emojis[emojiId];
          if (skinId <= emoji.skins.length - 1) {
            return emoji.skins[skinId].native;
          }

          return emoji.skins[SKINS_ID.NO_SKIN].native;
        }),
      }));
    return result;
  };

  const fetchEmoji = () => new Promise((resolve, reject) => {
    const localEmojiData = localStorage.getItem(EMOJI_LOCALSTORAGE_KEY.EMOJI_DATA);
    if (localEmojiData) {
      resolve(JSON.parse(localEmojiData));
    }

    fetch(EMOJI_CDN)
      .then((res) => res.json())
      .then((res) => {
        localStorage.setItem(EMOJI_LOCALSTORAGE_KEY.EMOJI_DATA, JSON.stringify(res));
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });

  const initEmoji = async () => {
    const emojiData = await fetchEmoji().then((res) => res);
    const transformedEmojiData = transformToCategoryView(emojiData, SKINS_ID.NO_SKIN);
    setListEmoji(transformedEmojiData);

    const recentEmoji = JSON.parse(localStorage.getItem(EMOJI_LOCALSTORAGE_KEY.EMOJI_RECENT)) || [];
    if (recentEmoji) {
      setRecentListEmoji(recentEmoji);
    }
  };

  const handleSelectEmoji = (emoji) => {
    const recentEmoji = JSON.parse(localStorage.getItem(EMOJI_LOCALSTORAGE_KEY.EMOJI_RECENT)) || [];

    let emojis = [
      // @ts-ignore
      ...recentEmoji,
    ];

    const isEmojiUnavailable = !recentEmoji.includes(emoji);

    if (isEmojiUnavailable && recentEmoji.length < LIMIT_FREQUENT_EMOJIS) {
      emojis = [
        `${emoji}`,
        ...emojis,
      ];
    } else if (isEmojiUnavailable && recentEmoji.length === 30) {
      emojis.splice(recentEmoji.length - 1, 1);
      emojis = [
        `${emoji}`,
        ...emojis,
      ];
    }
    localStorage.setItem(EMOJI_LOCALSTORAGE_KEY.EMOJI_RECENT, JSON.stringify(emojis));
    setRecentListEmoji(emojis);
  };

  const onChangeKeyword = (keyword) => {
    setSearchKeyword(keyword);
  };

  const handleSearchEmoji = async (keyword) => {
    if (keyword.length > 0) {
      const emojiData = await fetchEmoji().then((res) => res);
      // @ts-ignore
      const emojiDataArray = Object.keys(emojiData.emojis).map((id) => emojiData.emojis[id]);
      const searchResult = emojiDataArray.filter((emoji) =>
        emoji.keywords.some((emojiKeyword) => emojiKeyword.includes(keyword)));
      setListEmoji([{
        id: null,
        emojis: searchResult.map((emoji) => emoji.skins[SKINS_ID.NO_SKIN].native),
      }]);
    } else {
      initEmoji();
    }
  };

  const handleScrollEmojiList = () => {
    const container = document.getElementById(EMOJI_LIST_ID);
    if (!container) return;
    const newScrollPos = container.scrollTop;

    let newIndexActiveCategory = activeIndexCategory;
    const nextIndexCategory = activeIndexCategory + 1;
    const prevIndexCategory = activeIndexCategory > 0 ? activeIndexCategory - 1 : 0;

    if (lastScrollPos.current <= newScrollPos) {
      newIndexActiveCategory = nextIndexCategory;
    } else {
      newIndexActiveCategory = prevIndexCategory;
    }

    lastScrollPos.current = newScrollPos;

    const options = {
      root: container,
      rootMargin: '0px',
      threshold: 1.0,
    };

    const observer = new IntersectionObserver((param) => {
      if (param[0].intersectionRatio <= 0
        || (newIndexActiveCategory === 0
        && container.scrollTop <= 100)) {
        setActiveIndexCategory(newIndexActiveCategory);
      }
    }, options);

    const target = document.getElementById(`section-${EMOJI_PICKER_CATEGORY[activeIndexCategory]}`);
    if (!target) return;

    if (newScrollPos > 0) {
      observer?.observe(target);
    } else if (newScrollPos === 0) {
      observer?.unobserve(target);
    }
  };

  useEffect(() => {
    initEmoji();
  }, []);

  useEffect(() => {
    handleSearchEmoji(searchKeyword);
  }, [keywordDebounce]);

  return {
    listEmoji,
    recentListEmoji,
    activeIndexCategory,
    handleClickCategory,
    handleSelectEmoji,
    handleScrollEmojiList,
    onChangeKeyword,
  };
}

export default useEmojiPicker;
